import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, filter, map, of, switchMap } from 'rxjs';
import { ChatAdminApiService } from '../../services/chat-admin-api.service';
import { LiveChatActions } from '../live-chat/live-chat.actions';
import { MessagesActions } from '../messages/messages.actions';
import { ChatAdminActions } from './chat-admin.actions';
import { selectChatAdminState, selectContextName } from './chat-admin.reducer';
import { OverlayActions } from '../overlay-state/overlay.actions';
import { isUserSignedIn } from '../../../utils/userProfile';
import { AuthTokenActions } from '../auth-token/auth-token.actions';
import { selectSessionIdentifier } from '../va-session/va-session.reducer';
import { selectLanguage } from '../settings/settings.reducers';
import { EscalationType, OverlayType, RealmName } from '../../types/enum';
import { getBrand } from '../../../utils/getBrand';

@Injectable({
  providedIn: 'root',
})
export class ChatAdminEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private chatAdminApiService: ChatAdminApiService
  ) {}

  getChatAdminResponse = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatAdminActions.callCustomerJourney),
      concatLatestFrom(() => [
        this.store.select(selectLanguage),
        this.store.select(selectSessionIdentifier),
      ]),
      switchMap(([action, language, sessionId]) => {
        const isFirstCall = action.isFirstCall;
        const beforeEscalation = action.beforeEscalation;
        return this.chatAdminApiService
          .callCustomerJourney({
            contextName: action.contextName,
            isFirstCall,
            language,
            sessionId,
          })
          .pipe(
            map((reponse) =>
              ChatAdminActions.updateResponse({
                value: reponse,
                isFirstCall,
                beforeEscalation,
              })
            ),
            catchError(() => of(ChatAdminActions.handleChatAdminFailure()))
          );
      })
    )
  );

  chatAdminResponseSucces$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatAdminActions.updateResponse),
      concatLatestFrom(() => this.store.select(selectSessionIdentifier)),
      switchMap(([action, sessionId]) => {
        const isFirstCall = action.isFirstCall;
        const beforeEscalation = action.beforeEscalation;
        if (sessionId && (isFirstCall || !beforeEscalation)) {
          if (action.value.isVa) {
            const initialQuery = action.value.initialQuery;
            return of(MessagesActions.callDialogApi({ query: initialQuery }));
          }

          if (
            isFirstCall &&
            !isUserSignedIn() &&
            getBrand().name != RealmName.shaw
          ) {
            return of(MessagesActions.askUserToLogin());
          }
        }
        return of(ChatAdminActions.escalate());
      })
    )
  );

  loadAlertBanner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatAdminActions.updateResponse),
      filter((action) => action.value.alertConfig && action.isFirstCall),
      map((action) =>
        ChatAdminActions.loadAlertBanner({
          alertConfig: action.value.alertConfig,
        })
      )
    )
  );

  escalateFromChatAdmin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatAdminActions.escalate, MessagesActions.dialogApiFailed),
      concatLatestFrom(() => this.store.select(selectChatAdminState)),
      switchMap(([, chatAdminResponse]) => {
        if (chatAdminResponse?.escalationType === EscalationType.Curated) {
          return of(
            OverlayActions.showOverlay({
              value: OverlayType.PostEscalation,
            })
          );
        }
        if (chatAdminResponse?.escalationType === EscalationType.Direct) {
          return of(
            LiveChatActions.escalateToLiveChat({
              departmentId: chatAdminResponse.directEscalation?.departmentId,
              platform: chatAdminResponse.directEscalation.platform,
              aaEligible: chatAdminResponse.directEscalation.aaEligible,
            })
          );
        }
      })
    )
  );

  handleChatAdminFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChatAdminActions.handleChatAdminFailure),
      concatLatestFrom(() => this.store.select(selectContextName)),
      switchMap(([, contextName]) => {
        if (contextName) {
          return of(ChatAdminActions.escalate());
        }
        return of(ChatAdminActions.getDefaultJourney());
      })
    )
  );

  getDefaultChatAdminResponse = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ChatAdminActions.getDefaultJourney,
        AuthTokenActions.handleSessionFailure
      ),
      switchMap(() => this.chatAdminApiService.getDefaultJourney()),
      switchMap((reponse) => {
        return of(ChatAdminActions.updateResponse({ value: reponse }));
      })
    )
  );
}
